'use client';

/**
 * Audio playback hook — manages the agent's voice response queue.
 *
 * Receives base64-encoded PCM audio chunks from the WebSocket,
 * decodes them to AudioBuffers, and schedules sequential playback
 * through an AnalyserNode for visualization.
 *
 * Audio chain:
 *   BufferSource → AnalyserNode → MediaStreamAudioDestinationNode
 *     → hidden <audio srcObject> element
 *
 * See src/lib/audio-sink.ts's header for why the chain ends in a hidden
 * <audio> element (AEC reference coverage) rather than
 * AudioContext.destination — that recipe (and its rationale) is shared
 * with the [rtc-spike] transport in useCallMode.ts, so it lives there now.
 *
 * The element needs a user gesture to start playing (autoplay policy),
 * and this chain used to be built lazily on the first audio chunk —
 * mid-call, long after the tap. `primePlayback()` exists so startCall
 * can build + unlock the whole chain inside the click, exactly like the
 * capture context's gesture-time creation (FB19).
 *
 * This hook owns the AnalyserNode's lifecycle (create on prime/first
 * chunk, disconnect on clear) and hands it to the audio level engine via
 * `registerAgentAnalyser` — the engine's single rAF loop (see
 * useAudioVisualization.ts) reads it from there.
 */

import { useRef, useCallback } from 'react';
import { createHiddenAudioSink } from '@/lib/audio-sink';

// ─── Hook ───────────────────────────────────────────────────────

export function useAudioPlayback(
  /** Hands the freshly-created (or, on clear, null) agent AnalyserNode to
   *  the audio level engine. See useAudioVisualization.ts. */
  registerAgentAnalyser: (analyser: AnalyserNode | null) => void
) {
  const playbackAudioContextRef = useRef<AudioContext | null>(null);
  const playbackAnalyserRef = useRef<AnalyserNode | null>(null);
  const streamDestinationRef = useRef<MediaStreamAudioDestinationNode | null>(
    null
  );
  const sinkElementRef = useRef<HTMLAudioElement | null>(null);
  const audioQueueRef = useRef<string[]>([]);
  const isPlayingRef = useRef(false);
  const nextAudioStartTimeRef = useRef(0);

  /** Build the context → analyser → stream-destination → <audio> chain if it
   *  doesn't exist yet. Idempotent; safe to call per chunk. */
  const ensurePlaybackChain = useCallback(() => {
    if (playbackAudioContextRef.current) return;

    const ctx = new AudioContext({ sampleRate: 24000 });
    playbackAudioContextRef.current = ctx;

    const analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;

    // Final hop through a media element — see src/lib/audio-sink.ts's
    // header for the WHY. The element lives hidden in the DOM (detached
    // elements are flaky about MediaStream playback on WebKit).
    const streamDestination = ctx.createMediaStreamDestination();
    analyser.connect(streamDestination);
    streamDestinationRef.current = streamDestination;

    const sink = createHiddenAudioSink(
      '[voice] playback',
      streamDestination.stream
    );
    sinkElementRef.current = sink;

    playbackAnalyserRef.current = analyser;
    registerAgentAnalyser(analyser);
  }, [registerAgentAnalyser]);

  /** Build + unlock the playback chain inside a user gesture (call start).
   *  Both the AudioContext and the <audio> element inherit the gesture's
   *  activation, so neither can come up suspended/autoplay-blocked the way
   *  a lazy first-chunk build can. */
  const primePlayback = useCallback(() => {
    ensurePlaybackChain();
    const ctx = playbackAudioContextRef.current;
    if (ctx?.state === 'suspended') {
      void ctx.resume().catch(() => undefined);
    }
    void sinkElementRef.current?.play().catch(() => undefined);
  }, [ensurePlaybackChain]);

  const processAudioQueue = useCallback(async () => {
    // Re-entrancy guard. enqueueAudio fires this on every chunk. Today the
    // drain below is fully synchronous (no await), so overlapping calls can't
    // actually interleave — but this guard keeps the queue correct if an await
    // (e.g. decodeAudioData) is ever introduced: without it, two drains would
    // race nextAudioStartTimeRef and the shared queue and schedule chunks at
    // overlapping startTimes (doubled/out-of-order voice). The try/finally
    // ensures the flag clears even if decoding throws.
    if (isPlayingRef.current) return;
    isPlayingRef.current = true;

    try {
      // Normally already built by primePlayback inside the call-start
      // gesture; this lazy fallback keeps playback working for any audio
      // that arrives outside that path.
      ensurePlaybackChain();

      const ctx = playbackAudioContextRef.current!;
      const analyser = playbackAnalyserRef.current;

      // If the context was created lazily — outside any user gesture — the
      // browser's autoplay policy may have started it 'suspended' (no sound,
      // currentTime frozen). Nudge it awake; buffers scheduled below start
      // playing the moment it transitions to running.
      if (ctx.state === 'suspended') {
        console.warn('[voice] playback context suspended — resuming');
        void ctx.resume().catch(() => undefined);
      }

      let startTime = nextAudioStartTimeRef.current;
      if (!startTime || startTime < ctx.currentTime) {
        startTime = ctx.currentTime;
      }

      while (audioQueueRef.current.length > 0) {
        const base64 = audioQueueRef.current.shift()!;

        // Decode base64 → Int16 PCM → Float32 → AudioBuffer
        const binaryString = atob(base64);
        const len = binaryString.length;
        const uint8 = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
          uint8[i] = binaryString.charCodeAt(i);
        }

        try {
          const int16 = new Int16Array(uint8.buffer);
          const float32 = new Float32Array(int16.length);
          for (let i = 0; i < int16.length; i++) {
            float32[i] = int16[i] / 32768;
          }

          const buffer = ctx.createBuffer(1, float32.length, 24000);
          buffer.copyToChannel(float32, 0);
          const source = ctx.createBufferSource();
          source.buffer = buffer;
          source.connect(analyser!);
          source.start(startTime);
          startTime += buffer.duration;
        } catch (err) {
          console.error('Error decoding/playing audio', err);
        }
      }

      nextAudioStartTimeRef.current = startTime;
    } finally {
      isPlayingRef.current = false;
    }
  }, [ensurePlaybackChain]);

  const enqueueAudio = useCallback(
    (audioBase64: string) => {
      audioQueueRef.current.push(audioBase64);
      processAudioQueue();
    },
    [processAudioQueue]
  );

  /** Clear the audio queue and tear down the playback chain (context,
   *  stream destination, and the hidden <audio> sink element). */
  const clearPlayback = useCallback(() => {
    audioQueueRef.current = [];
    nextAudioStartTimeRef.current = 0;
    isPlayingRef.current = false;

    playbackAnalyserRef.current?.disconnect();
    playbackAnalyserRef.current = null;
    registerAgentAnalyser(null);

    const sink = sinkElementRef.current;
    if (sink) {
      sink.pause();
      sink.srcObject = null;
      sink.remove();
      sinkElementRef.current = null;
    }
    streamDestinationRef.current?.disconnect();
    streamDestinationRef.current = null;

    if (playbackAudioContextRef.current) {
      try {
        playbackAudioContextRef.current.close();
      } catch {
        // Already closed
      }
      playbackAudioContextRef.current = null;
    }
  }, [registerAgentAnalyser]);

  return {
    enqueueAudio,
    clearPlayback,
    primePlayback,
    audioQueueRef,
    playbackAudioContextRef,
    isPlayingRef,
  };
}
